import { useEffect, useState } from "react";

import { ACTION_TYPES } from "../../../store/actionTypes";
import { FLOW_TYPES } from "../../../constants/flowType";
import { useTicketingContext } from "../../../store";
import { HELPERS } from "../../../utils";
import { InputNumberField, TimeslotSelect } from "../..";

const ActivitiesController = ({
  addonId,
  timeslots,
  onControlChange,
  initialTimeslot = null,
  isLastElement = false,
  addon,
}) => {
  const [isInitiallySet, setIsInitiallySet] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState(initialTimeslot);
  const [selectedQuantity, setSelectedQuantity] = useState(0);

  const { state, dispatch } = useTicketingContext();
  const { addons, texts, flowType, summary } = state;
  const { selectedDate, selectedAddonTickets } = summary;
  const event = state.addons.find((addon) => addon.id === addonId);
  const isAutoSelect = Boolean(addon?.auto_select_timeslot);
  const isDisplayOnly = Number(addon?.display_only) === 1;
  const isSpecialEvent =
    event.admission_code?.toUpperCase()?.replace(" ", "") !== "BEERTASTING" &&
    event.admission_code?.toUpperCase()?.replace(" ", "") !== "GUIDEDTOUR";

  useEffect(() => {
    if (!isInitiallySet && Object.keys(selectedAddonTickets).length > 0) {
      const addon = selectedAddonTickets[addonId];
      if (addon) {
        let timeslot;
        if (flowType === FLOW_TYPES.ACTIVITIES_ONLY) {
          const addonTimeslots = addons
            .find((adn) => adn.id === addonId)
            ?.dates?.flatMap((date) =>
              date?.timeslots.flatMap((timeslot) => timeslot)
            );
          timeslot = addonTimeslots.find(
            (ts) => ts.entry_no === addon.entry_no
          );
        } else {
          timeslot = addons
            .find((adn) => adn.id === addonId)
            ?.dates?.find((date) => date.date === selectedDate)
            ?.timeslots.find((ts) => ts.entry_no === addon.entry_no);
        }

        if (addon) {
          setSelectedTimeslot([timeslot]);
          setSelectedQuantity(addon.qty);
        }
      }

      setIsInitiallySet(true);
    }
  }, [isInitiallySet, selectedAddonTickets]);

  useEffect(() => {
    let actControlerResponse;
    if (!selectedTimeslot && !isAutoSelect) {
      actControlerResponse = {};
    } else if (!selectedTimeslot && isAutoSelect) {
      const activeTimeslot = timeslots?.find((timeslot) => timeslot.active);
      actControlerResponse = {
        entry_no: activeTimeslot?.entry_no,
        qty: selectedQuantity,
      };
      onControlChange(actControlerResponse);
    } else {
      actControlerResponse = {
        entry_no: selectedTimeslot?.[0]?.entry_no,
        qty: selectedQuantity,
      };
      onControlChange(actControlerResponse);
    }
  }, [selectedTimeslot, selectedQuantity]);

  useEffect(() => {
    if (flowType === FLOW_TYPES.ACTIVITIES_ONLY) {
      if (Object.keys(selectedAddonTickets).length === 0) {
        setSelectedQuantity(0);
        setSelectedTimeslot(null);
      }
    }
    if (
      selectedTimeslot &&
      selectedTimeslot[0]?.active === "false" &&
      !isSpecialEvent
    ) {
      setSelectedQuantity(0);
    }
  }, [selectedAddonTickets]);

  const onTimeSlotSelectHandler = (timeslot) => {
    const selectedTimeSlot = timeslots.filter(
      (tm) => tm?.timestamp_in_milis === timeslot?.value
    );
    setSelectedTimeslot(selectedTimeSlot);
    if (
      selectedQuantity !== 0 &&
      state.selectedTimeslots.length > 0 &&
      !state.selectedTimeslots.find(
        (time) => time.entry_no === timeslot.entry_no
      )
    ) {
      setSelectedQuantity(0);
    }
  };

  const onTimeSlotSelectActivitiesOnlyHandler = (timeslot) => {
    const selectedTimeSlot = timeslots.filter(
      (tm) => tm?.timestamp_in_milis === timeslot?.value
    );

    let newQuantity = selectedQuantity;

    if (
      selectedQuantity === 0 ||
      (state.selectedTimeslots.length > 0 &&
        !state.selectedTimeslots.find(
          (time) => time.entry_no === timeslot.entry_no
        ))
    ) {
      newQuantity = 1;
    }

    setSelectedTimeslot(selectedTimeSlot);
    setSelectedQuantity(newQuantity);
  };

  const onInputChangeValueHandler = (newValue) => {
    setSelectedQuantity(newValue);
    if (selectedTimeslot && selectedTimeslot[0]) {
      if (newValue > 0) {
        dispatch({
          type: ACTION_TYPES.ADD_SELECTED_TIMESLOT,
          payload: {
            timeSlot: { ...selectedTimeslot[0], addonId },
          },
        });
      } else {
        dispatch({
          type: ACTION_TYPES.REMOVE_SELECTED_TIMESLOT,
          payload: selectedTimeslot[0],
        });
      }
    }
  };

  const increaseQtyHandler = (amount = 1) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          items: [
            {
              item_id: event.id,
              item_name: event.title,
              quantity: amount,
            },
          ],
        },
      });
    }
  };

  const decreaseQtyHandler = (amount = 1) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "remove_from_cart",
        ecommerce: {
          items: [
            {
              item_id: event.id,
              item_name: event.title,
              quantity: amount,
            },
          ],
        },
      });
    }
  };

  const isInputDisabled = isAutoSelect
    ? addon?.max_qty === 0
    : !selectedTimeslot ||
      (selectedTimeslot &&
        HELPERS.checkIsTimeslotTaken(
          selectedTimeslot[0],
          state.selectedTimeslots,
          addonId,
          flowType === FLOW_TYPES.ACTIVITIES_ONLY
        ));

  const allowedAmount = HELPERS.calculateAllowedActivityAmount(addonId, state);
  const ticketsLeft = selectedTimeslot?.[0]?.tickets_left || 0;
  const disableButton = allowedAmount > ticketsLeft || isInputDisabled;

  if (isDisplayOnly) {
    return null;
  }

  return (
    <div className="addons-control">
      {!isAutoSelect ? (
        <div className="addons-control-timeslot-select">
          <TimeslotSelect
            addonId={addonId}
            allowedAmount={allowedAmount}
            defaultValue={selectedTimeslot?.[0]?.timestamp_in_milis}
            isLastElement={isLastElement}
            onTimeSlotSelect={
              flowType === FLOW_TYPES.ACTIVITIES_ONLY
                ? onTimeSlotSelectActivitiesOnlyHandler
                : onTimeSlotSelectHandler
            }
            timeslots={timeslots}
          />
        </div>
      ) : null}
      {flowType === FLOW_TYPES.ACTIVITIES_ONLY ? (
        <InputNumberField
          className={`addons-add-button addons-add-button--activities ${
            isInputDisabled ? "disabled" : ""
          } ${
            selectedTimeslot && !isAutoSelect && "addons-add-button--selected"
          }`}
          decreaseQtyHandler={decreaseQtyHandler}
          disabled={isInputDisabled}
          increaseQtyHandler={increaseQtyHandler}
          initialValue={selectedQuantity}
          inputMax={
            isAutoSelect ? addon?.max_qty : selectedTimeslot?.[0]?.tickets_left
          }
          inputMin={isAutoSelect ? addon?.min_qty : 0}
          onChangeValue={onInputChangeValueHandler}
        />
      ) : isSpecialEvent ? (
        <InputNumberField
          className={`addons-add-button addons-add-button--activities ${
            isInputDisabled ? "disabled" : ""
          } ${
            selectedTimeslot && !isAutoSelect && "addons-add-button--selected"
          }`}
          decreaseQtyHandler={decreaseQtyHandler}
          disabled={isInputDisabled}
          increaseQtyHandler={increaseQtyHandler}
          initialValue={selectedQuantity}
          inputMax={
            isAutoSelect ? addon?.max_qty : selectedTimeslot?.[0]?.tickets_left
          }
          inputMin={isAutoSelect ? addon?.min_qty : 0}
          onChangeValue={onInputChangeValueHandler}
        />
      ) : (
        <div
          className={`addons-add-button ${disableButton ? "disabled" : ""} ${
            !disableButton &&
            (selectedQuantity > 0
              ? "addons-add-button--clicked"
              : "addons-add-button--selected")
          }`}
          onClick={() => {
            if (!disableButton) {
              if (selectedQuantity > 0) {
                decreaseQtyHandler(allowedAmount);
                setSelectedQuantity(0);
                if (selectedTimeslot && selectedTimeslot[0]) {
                  dispatch({
                    type: ACTION_TYPES.REMOVE_SELECTED_TIMESLOT,
                    payload: selectedTimeslot[0],
                  });
                }
              } else {
                increaseQtyHandler(allowedAmount);
                setSelectedQuantity(allowedAmount);
                dispatch({
                  type: ACTION_TYPES.ADD_SELECTED_TIMESLOT,
                  payload: {
                    timeSlot: { ...selectedTimeslot[0], addonId },
                  },
                });
              }
            }
          }}
        >
          {selectedQuantity > 0
            ? `${texts?.steps?.[2]?.remove || "Remove"} ${allowedAmount}`
            : `${texts?.steps?.[2]?.add || "Add"} ${allowedAmount}`}
        </div>
      )}
    </div>
  );
};

export default ActivitiesController;
